import React, { useState, useEffect } from "react";
import "./AdminPostManagement.css";
import { adminFetch } from "./utils/adminApi";

const AdminTransactionManagement = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [filter, setFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [transactionsPerPage] = useState(15);
  const [stats, setStats] = useState({
    totalTransactions: 0,
    totalAmount: 0,
    completedTransactions: 0,
    pendingTransactions: 0,
    revenueToday: 0,
    revenueThisMonth: 0,
    avgTransactionValue: 0,
  });

  const indexOfLastTransaction = currentPage * transactionsPerPage;
  const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPage;
  const currentTransactions = filteredTransactions.slice(
    indexOfFirstTransaction,
    indexOfLastTransaction
  );

  const totalPages = Math.ceil(
    filteredTransactions.length / transactionsPerPage
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(amount);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleDateString("vi-VN", {
      timeZone: "Asia/Ho_Chi_Minh",
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const result = await adminFetch("/api/admin/transactions?limit=2000");
        const rows = result.data || [];
        setTransactions(rows);
        setFilteredTransactions(rows);
        setStats(
          result.summary || {
            totalTransactions: 0,
            totalAmount: 0,
            completedTransactions: 0,
            pendingTransactions: 0,
            revenueToday: 0,
            revenueThisMonth: 0,
            avgTransactionValue: 0,
          }
        );
      } catch (error) {
        console.error("Lỗi khi tải dữ liệu giao dịch:", error);
        setTransactions([]);
        setFilteredTransactions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, []);

  useEffect(() => {
    let results = [...transactions];

    if (filter !== "all") {
      results = results.filter((transaction) => {
        const status = String(transaction.status || "").toLowerCase();
        if (filter === "completed") {
          return status === "completed" || status === "paid";
        } else if (filter === "pending") {
          return status !== "completed" && status !== "paid";
        }
        return true;
      });
    }

    if (searchText.trim() !== "") {
      const searchLower = searchText.toLowerCase();
      results = results.filter((transaction) => {
        return (
          (transaction.id &&
            transaction.id.toLowerCase().includes(searchLower)) ||
          (transaction.userId &&
            transaction.userId.toLowerCase().includes(searchLower)) ||
          (transaction.itemName &&
            transaction.itemName.toLowerCase().includes(searchLower)) ||
          (transaction.paymentInfo?.reference &&
            transaction.paymentInfo.reference
              .toLowerCase()
              .includes(searchLower))
        );
      });
    }

    if (fromDate || toDate) {
      results = results.filter((transaction) => {
        if (!transaction.updatedAt) return false;

        const transactionDate = new Date(transaction.updatedAt);
        const transactionDateStr = new Intl.DateTimeFormat("en-CA", {
          timeZone: "Asia/Ho_Chi_Minh",
          year: "numeric",
          month: "2-digit",
          day: "2-digit"
        }).format(transactionDate);

        if (fromDate && toDate) {
          return transactionDateStr >= fromDate && transactionDateStr <= toDate;
        } else if (fromDate) {
          return transactionDateStr >= fromDate;
        } else if (toDate) {
          return transactionDateStr <= toDate;
        }
        return true;
      });
    }

    setFilteredTransactions(results);
    setCurrentPage(1);
  }, [searchText, filter, transactions, fromDate, toDate]);

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-4">Quản lý giao dịch</h2>
      <div className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="text-sm font-medium text-gray-500">
              Tổng giao dịch
            </h3>
            <p className="text-2xl font-bold text-gray-900 mt-2">
              {stats.totalTransactions}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="text-sm font-medium text-gray-500">Doanh thu</h3>
            <p className="text-2xl font-bold text-gray-900 mt-2">
              {formatCurrency(stats.totalAmount)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="text-sm font-medium text-gray-500">Hoàn thành</h3>
            <p className="text-2xl font-bold text-gray-900 mt-2">
              {stats.completedTransactions}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="text-sm font-medium text-gray-500">Đang xử lý</h3>
            <p className="text-2xl font-bold text-gray-900 mt-2">
              {stats.pendingTransactions}
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex flex-wrap gap-4">
          <input
            type="text"
            placeholder="Tìm kiếm theo ID, người dùng, trạng thái..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="px-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="px-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="px-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">Tất cả</option>
            <option value="completed">Hoàn thành</option>
            <option value="pending">Đang xử lý</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white">
          <thead>
            <tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal">
              <th className="py-3 px-6 text-left">ID</th>
              <th className="py-3 px-6 text-left">Người dùng</th>
              <th className="py-3 px-6 text-left">Tên gói</th>
              <th className="py-3 px-6 text-left">Số tiền</th>
              <th className="py-3 px-6 text-left">Trạng thái</th>
              <th className="py-3 px-6 text-left">Ngày</th>
            </tr>
          </thead>
          <tbody className="text-gray-600 text-sm">
            {currentTransactions.length > 0 ? (
              currentTransactions.map((transaction) => (
                <tr
                  key={transaction.id}
                  className="border-b border-gray-200 hover:bg-gray-50"
                >
                  <td className="py-3 px-6 text-left">{transaction.id}</td>
                  <td className="py-3 px-6 text-left">
                    {transaction.userId || "N/A"}
                  </td>
                  <td className="py-3 px-6 text-left">
                    {transaction.itemName || "N/A"}
                  </td>
                  <td className="py-3 px-6 text-left">
                    {formatCurrency(transaction.amount)}
                  </td>
                  <td className="py-3 px-6 text-left">
                    {transaction.status || "N/A"}
                  </td>
                  <td className="py-3 px-6 text-left">
                    {formatDate(transaction.updatedAt)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="py-6 text-center text-gray-500">
                  Không tìm thấy giao dịch nào
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminTransactionManagement;
